"use strict";Object.defineProperty(exports, "__esModule", {value: true}); function _nullishCoalesce(lhs, rhsFn) { if (lhs != null) { return lhs; } else { return rhsFn(); } } function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }var _react = require('react');
var _errors = require('shared/errors');
var _uploadFailure = require('shared/services/uploadFailure');


// The one shape every file-upload progress UI reads. Whatever moves the bytes
// (today the XHR upload in RestFetch; later a native background uploader)
// writes it through an adapter, so the UI never knows the transport.
//
//   preparing  the upload started but no byte has gone out (CSRF, file open),
//              or the file itself is still being made (a video compressing);
//              `prepared` is then how far along that is, 0–1, when known
//   uploading  bytes are going out: `sent` of `total`
//   finishing  every byte is sent; waiting for the server to store the file
//              and the message to be posted
//   failed     it stopped; `failure` says why, `retryable` whether Retry can
//              resend the same (already compressed) file
//   sent       the server confirmed; the entry is cleared shortly after



























const entries = new Map()
const listeners = new Map()

const notify = (key) => _optionalChain([listeners, 'access', _ => _.get, 'call', _2 => _2(key), 'optionalAccess', _3 => _3.forEach, 'call', _4 => _4(listener => listener())])

 const uploadProgressStore = {
  get: (key) => entries.get(key),

  set: (key, progress) => {
    if (progress) entries.set(key, progress)
    else if (!entries.delete(key)) return
    notify(key)
  },

  subscribe: (key, listener) => {
    let set = listeners.get(key)
    if (!set) {
      set = new Set()
      listeners.set(key, set)
    }
    set.add(listener)
    return () => {
      _optionalChain([set, 'optionalAccess', _5 => _5.delete, 'call', _6 => _6(listener)])
      if (_optionalChain([set, 'optionalAccess', _7 => _7.size]) === 0) listeners.delete(key)
    }
  },
}; exports.uploadProgressStore = uploadProgressStore

/** The live upload state of one message, or undefined when none is tracked. */
 const useUploadProgress = (key) => {
  const subscribe = _react.useCallback.call(void 0, 
    (listener) => (key ? exports.uploadProgressStore.subscribe(key, listener) : () => undefined),
    [key],
  )
  const getSnapshot = _react.useCallback.call(void 0, () => (key ? exports.uploadProgressStore.get(key) : undefined), [key])
  return _react.useSyncExternalStore.call(void 0, subscribe, getSnapshot, getSnapshot)
}; exports.useUploadProgress = useUploadProgress

// Progress events arrive per network chunk. Publishing each one would
// re-render the bubble hundreds of times a second, so only whole-percent steps
// (and the first and last byte) are published.
 const shouldPublishProgress = (previous, sent, total) => {
  if (!previous || previous.phase !== 'uploading') return true
  if (total <= 0) return sent - previous.sent >= 256 * 1024
  if (sent >= total) return true
  return Math.floor((sent / total) * 100) > Math.floor((previous.sent / Math.max(previous.total, 1)) * 100)
}; exports.shouldPublishProgress = shouldPublishProgress

 const progressFromBytes = (sent, total) => ({
  phase: total > 0 && sent >= total ? 'finishing' : 'uploading',
  sent,
  total,
  retryable: false,
}); exports.progressFromBytes = progressFromBytes

 const toUploadPercent = (progress) => {
  if (!(progress.total > 0)) return undefined
  return Math.min(100, Math.max(0, Math.floor((progress.sent / progress.total) * 100)))
}; exports.toUploadPercent = toUploadPercent

// Decimal megabytes, as iOS and Android show file sizes.
const MB = 1000000

const formatMb = (bytes, decimals) => {
  const value = bytes / MB
  return value.toFixed(decimals).replace(/\.0+$/, '')
}

/** "34 of 73 MB"; one decimal below 10 MB ("2.4 of 6.7 MB"). */
 const formatUploadBytes = (sent, total) => {
  if (!(total > 0)) return undefined
  const decimals = total < 10 * MB ? 1 : 0
  return `${formatMb(Math.min(sent, total), decimals)} of ${formatMb(total, decimals)} MB`
}; exports.formatUploadBytes = formatUploadBytes

const httpStatusOf = (err) => {
  if (!(err instanceof _errors.RestError)) return undefined
  const status = _optionalChain([(err.response ), 'optionalAccess', _8 => _8.status])
  return typeof status === 'number' ? status : undefined
}

 const UPLOAD_SOURCE_MISSING = 'UploadSourceMissing'; exports.UPLOAD_SOURCE_MISSING = UPLOAD_SOURCE_MISSING
// The name of the error a part's `ready` rejects with when its file could not
// be made (sendConversationAttachments).
 const MEDIA_NOT_PREPARED = 'MediaNotPrepared'; exports.MEDIA_NOT_PREPARED = MEDIA_NOT_PREPARED

 const classifyUploadFailure = (err, context) => {
  if (err instanceof Error && err.name === exports.UPLOAD_SOURCE_MISSING) return 'missing'
  if (err instanceof Error && err.name === exports.MEDIA_NOT_PREPARED) return 'unprepared'
  if (_uploadFailure.isUploadTooLarge.call(void 0, err)) return 'too_large'
  if (context.timedOut) return 'timeout'
  const status = httpStatusOf(err)
  if (status !== undefined) {
    if (status >= 500) return 'server'
    if (status >= 400) return 'rejected'
    return 'unknown'
  }
  if (err instanceof _errors.RestError && _optionalChain([err, 'access', _9 => _9.response, 'optionalAccess', _10 => _10.Transient])) return 'offline'
  if (err instanceof _errors.RestError && _optionalChain([err, 'access', _11 => _11.response, 'optionalAccess', _12 => _12.ServerError])) return 'server'
  return 'unknown'
}; exports.classifyUploadFailure = classifyUploadFailure

const FAILURE_LABELS = {
  offline: 'Upload interrupted',
  timeout: 'Upload timed out',
  rejected: 'Upload was refused',
  server: 'Server error',
  missing: 'File no longer on this device',
  not_sent: 'Not sent',
  unprepared: 'Video could not be prepared',
  too_large: _uploadFailure.UPLOAD_TOO_LARGE_MESSAGE,
  unknown: 'Could not be sent',
}

// Failures no Retry can fix: the same file would fail the same way.
const FINAL_FAILURES = new Set(['missing', 'unprepared', 'too_large'])
 const isRetryableUploadFailure = (failure) => !FINAL_FAILURES.has(failure); exports.isRetryableUploadFailure = isRetryableUploadFailure

/** Short reason shown before "Tap to retry". */
 const uploadFailureLabel = (failure) =>
  FAILURE_LABELS[_nullishCoalesce(failure, () => ( 'unknown'))]; exports.uploadFailureLabel = uploadFailureLabel

// How long "sent" stays visible before the entry is dropped; the bubble has
// switched to its delivered look by then.
const SENT_CLEAR_MS = 1500

/**
 * The adapter for today's transport: an XHR upload in RestFetch reports bytes
 * through the request's AbortSignal (`onUploadProgress`). Another uploader
 * (e.g. a native background module) only has to call these same methods.
 */
 const createUploadProgressTracker = (key, totalHint) => {
  const total = totalHint > 0 ? totalHint : 0
  exports.uploadProgressStore.set(key, { phase: 'preparing', sent: 0, total, retryable: false })

  return {
    bytes: (sent, reportedTotal) => {
      const size = reportedTotal > 0 ? reportedTotal : total
      const previous = exports.uploadProgressStore.get(key)
      if (_optionalChain([previous, 'optionalAccess', _13 => _13.phase]) === 'failed' || _optionalChain([previous, 'optionalAccess', _14 => _14.phase]) === 'sent') return
      if (!exports.shouldPublishProgress.call(void 0, previous, sent, size)) return
      exports.uploadProgressStore.set(key, exports.progressFromBytes.call(void 0, sent, size))
    },
    // The server answered the upload; the message send is still in flight.
    uploaded: () => {
      const previous = exports.uploadProgressStore.get(key)
      exports.uploadProgressStore.set(key, {
        phase: 'finishing',
        sent: _optionalChain([previous, 'optionalAccess', _15 => _15.total]) || total,
        total: _optionalChain([previous, 'optionalAccess', _16 => _16.total]) || total,
        retryable: false,
      })
    },
    sent: () => {
      const previous = exports.uploadProgressStore.get(key)
      exports.uploadProgressStore.set(key, { phase: 'sent', sent: _optionalChain([previous, 'optionalAccess', _17 => _17.total]) || total, total: _optionalChain([previous, 'optionalAccess', _18 => _18.total]) || total, retryable: false })
      setTimeout(() => {
        if (_optionalChain([exports.uploadProgressStore, 'access', _19 => _19.get, 'call', _20 => _20(key), 'optionalAccess', _21 => _21.phase]) === 'sent') exports.uploadProgressStore.set(key, null)
      }, SENT_CLEAR_MS)
    },
    failed: (failure, retryable) => {
      const previous = exports.uploadProgressStore.get(key)
      exports.uploadProgressStore.set(key, {
        phase: 'failed',
        sent: _nullishCoalesce(_optionalChain([previous, 'optionalAccess', _22 => _22.sent]), () => ( 0)),
        total: _optionalChain([previous, 'optionalAccess', _23 => _23.total]) || total,
        retryable,
        failure,
      })
    },
    // A user cancel: the message is pruned, so nothing is left to show.
    cleared: () => exports.uploadProgressStore.set(key, null),
  }
}; exports.createUploadProgressTracker = createUploadProgressTracker



/** How far a file still being made (a video compressing) has got, 0–1, for
 * its row; ignored once its upload is under way. */
 const reportPreparing = (key, fraction, restarted = false) => {
  const previous = exports.uploadProgressStore.get(key)
  if (_optionalChain([previous, 'optionalAccess', _24 => _24.phase]) !== 'preparing') return
  const prepared = Math.max(0, Math.min(1, fraction))
  // Never backwards, unless preparing started again (a restarted encode).
  if (!restarted && previous.prepared !== undefined && Math.floor(prepared * 100) <= Math.floor(previous.prepared * 100)) return
  exports.uploadProgressStore.set(key, { ...previous, prepared })
}; exports.reportPreparing = reportPreparing

/** One-line status for surfaces without a ring: "Uploading 34% · 34 of 73 MB", "Finishing…". */
 const uploadStatusLabel = (progress) => {
  if (!progress) return undefined
  if (progress.phase === 'finishing') return 'Finishing…'
  if (progress.phase !== 'uploading') return undefined
  const percent = exports.toUploadPercent.call(void 0, progress)
  const bytes = exports.formatUploadBytes.call(void 0, progress.sent, progress.total)
  return [percent !== undefined ? `Uploading ${percent}%` : 'Uploading', bytes].filter(Boolean).join(' · ')
}; exports.uploadStatusLabel = uploadStatusLabel
